import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Table, Spinner, Alert, Button, FormGroup, Label, Input, Row, Col,
  Modal, ModalHeader, ModalBody, ModalFooter,
} from '../../shim/reactstrap';
import apiClient from '../../api/ApiClient';
import { readPowerUserMode } from '../Profile';

// Re-read the Power User Mode flag from localStorage every time the
// table is rendered. We deliberately don't cache the value across
// renders because the user can flip the toggle on the /profile page
// and come straight back here — a stale "false" would silently hide
// the Edit buttons. The function itself is cheap (one localStorage
// read) and tolerates exceptions.
const isPowerUserModeActive = () => readPowerUserMode();

const PAGE_SIZE = 50;

const TABLE_OPTIONS = [
  { value: 'market-breadth', label: 'Market Breadth (Daily)' },
  { value: 'market-breadth-weekly', label: 'Market Breadth (Weekly)' },
  { value: 'bond-market-activity', label: 'Bond Market Activity' },
];

export const AdminCustomMarketData = () => {
  const [selectedTable, setSelectedTable] = useState('market-breadth');

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h2 className="mb-0">Custom Market Data</h2>
      </div>
      <FormGroup row className="mb-4">
        <Label for="tableSelector" sm={2}>Table</Label>
        <Col sm={4}>
          <Input
            type="select"
            id="tableSelector"
            value={selectedTable}
            onChange={e => setSelectedTable(e.target.value)}
          >
            {TABLE_OPTIONS.map(opt => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </Input>
        </Col>
      </FormGroup>

      {selectedTable === 'market-breadth' && <MarketBreadthTable />}
      {selectedTable === 'market-breadth-weekly' && <MarketBreadthWeeklyTable />}
      {selectedTable === 'bond-market-activity' && <BondMarketActivityTable />}
    </div>
  );
};

const BOND_CATEGORY_FALLBACK = ['AllSecurities', 'Convertibles', 'HighYield', 'InvestmentGrade'];

const BondMarketActivityTable = () => {
  const [rows, setRows] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [collecting, setCollecting] = useState(false);
  const [error, setError] = useState(null);
  const [collectMessage, setCollectMessage] = useState(null);
  const [page, setPage] = useState(0);
  const [categoryFilter, setCategoryFilter] = useState('');
  const [categories, setCategories] = useState(BOND_CATEGORY_FALLBACK);

  const totalPages = Math.ceil(totalCount / PAGE_SIZE);

  useEffect(() => {
    apiClient.getJson('/api/custom-market-data/bond/categories')
      .then(res => res.json())
      .then(data => setCategories(Array.isArray(data) && data.length > 0 ? data : BOND_CATEGORY_FALLBACK))
      .catch(() => setCategories(BOND_CATEGORY_FALLBACK));
  }, []);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const body = {
        Offset: page * PAGE_SIZE,
        Limit: PAGE_SIZE,
        SkipTotalCount: false,
        OrderBy: 'DateDesc',
      };
      if (categoryFilter) body.Category = categoryFilter;

      const res = await apiClient.postJson('/api/custom-market-data/bond/search', body);
      const data = await res.json();
      setRows(data.Items || []);
      setTotalCount(data.TotalCount || 0);
    } catch (err) {
      setError(err.message || 'Failed to load bond market activity data');
    } finally {
      setLoading(false);
    }
  }, [page, categoryFilter]);

  useEffect(() => {
    load();
  }, [load]);

  const handleCategoryFilterChange = e => {
    setCategoryFilter(e.target.value);
    setPage(0);
  };

  const handleCollect = async () => {
    try {
      setCollecting(true);
      setCollectMessage(null);
      setError(null);
      await apiClient.postJson('/api/custom-market-data/bond/collect', {});
      setCollectMessage('Collection job queued. Data will appear shortly.');
    } catch (err) {
      setError(err.message || 'Failed to queue collection job');
    } finally {
      setCollecting(false);
    }
  };

  const formatNumber = n => (n == null ? '—' : n.toLocaleString());
  const formatDate = d => d ? new Date(d).toLocaleDateString('en-CA') : '—';

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-2">
        <div className="d-flex align-items-center gap-2">
          <span className="text-muted small">
            {loading ? 'Loading…' : `${totalCount.toLocaleString()} records`}
          </span>
          <Input
            type="select"
            bsSize="sm"
            style={{ width: 'auto', minWidth: 180 }}
            value={categoryFilter}
            onChange={handleCategoryFilterChange}
            disabled={loading}
          >
            <option value="">&lt;All Categories&gt;</option>
            {categories.map(cat => (
              <option key={cat} value={cat}>{cat}</option>
            ))}
          </Input>
        </div>
        <div className="d-flex gap-2">
          <Button
            size="sm"
            color="primary"
            onClick={handleCollect}
            disabled={collecting || loading}
          >
            {collecting ? <><Spinner size="sm" className="me-1" />Queuing…</> : '↓ Collect'}
          </Button>
          <Button size="sm" color="secondary" outline onClick={load} disabled={loading}>
            ↻ Refresh
          </Button>
        </div>
      </div>

      <Alert color="success" isOpen={!!collectMessage} toggle={() => setCollectMessage(null)} fade={false} className="mb-2">
        {collectMessage}
      </Alert>
      <Alert color="danger" isOpen={!!error} toggle={() => setError(null)} fade={false}>{error}</Alert>

      {loading && rows.length === 0 ? (
        <div className="text-center py-5">
          <Spinner />
        </div>
      ) : (
        <>
          <Table bordered hover responsive size="sm">
            <thead className="table-dark">
              <tr>
                <th>Date</th>
                <th>Category</th>
                <th className="text-end">Total Issues</th>
                <th className="text-end">Advances</th>
                <th className="text-end">Declines</th>
                <th className="text-end">Unchanged</th>
                <th className="text-end">52W High</th>
                <th className="text-end">52W Low</th>
                <th className="text-end">Dollar Vol. (M)</th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 ? (
                <tr>
                  <td colSpan={9} className="text-center text-muted py-4">No data</td>
                </tr>
              ) : rows.map(row => (
                <tr key={`${row.Date}-${row.Category}`}>
                  <td>{formatDate(row.Date)}</td>
                  <td>{row.Category}</td>
                  <td className="text-end text-muted">{formatNumber(row.TotalIssuesTraded)}</td>
                  <td className="text-end text-success">{formatNumber(row.Advances)}</td>
                  <td className="text-end text-danger">{formatNumber(row.Declines)}</td>
                  <td className="text-end text-muted">{formatNumber(row.Unchanged)}</td>
                  <td className="text-end text-success fw-semibold">{formatNumber(row.FiftyTwoWeekHigh)}</td>
                  <td className="text-end text-danger fw-semibold">{formatNumber(row.FiftyTwoWeekLow)}</td>
                  <td className="text-end">{row.DollarVolume == null ? '—' : row.DollarVolume.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                </tr>
              ))}
            </tbody>
          </Table>

          {totalPages > 1 && (
            <Row className="align-items-center mt-2">
              <Col xs="auto">
                <Button
                  size="sm"
                  color="secondary"
                  outline
                  disabled={page === 0 || loading}
                  onClick={() => setPage(0)}
                >
                  « First
                </Button>
              </Col>
              <Col xs="auto">
                <Button
                  size="sm"
                  color="secondary"
                  outline
                  disabled={page === 0 || loading}
                  onClick={() => setPage(p => p - 1)}
                >
                  ‹ Prev
                </Button>
              </Col>
              <Col className="text-center text-muted small">
                Page {page + 1} of {totalPages}
              </Col>
              <Col xs="auto">
                <Button
                  size="sm"
                  color="secondary"
                  outline
                  disabled={page >= totalPages - 1 || loading}
                  onClick={() => setPage(p => p + 1)}
                >
                  Next ›
                </Button>
              </Col>
              <Col xs="auto">
                <Button
                  size="sm"
                  color="secondary"
                  outline
                  disabled={page >= totalPages - 1 || loading}
                  onClick={() => setPage(totalPages - 1)}
                >
                  Last »
                </Button>
              </Col>
            </Row>
          )}
        </>
      )}
    </div>
  );
};

// Blank form payload used by the Add / Edit modal for MarketBreadth.
// Dates are kept as ISO yyyy-MM-dd strings so the HTML5 date picker
// binds cleanly. Every numeric field starts as `''` so React renders
// the input as empty rather than 0.
//
// `Collected` is intentionally absent — the controller forces it to
// FALSE on every admin save, so the UI never carries a Collected
// value across the wire.
const emptyMarketBreadthForm = () => ({
  Date: '',
  Index: '',
  Advances: '',
  Declines: '',
  Unchanged: '',
  AdvancesVolume: '',
  DeclinesVolume: '',
  TotalVolume: '',
  NewHighs: '',
  NewLows: '',
  HiLo: '',
});

const MarketBreadthTable = () => {
  const [rows, setRows] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [collecting, setCollecting] = useState(false);
  const [error, setError] = useState(null);
  const [collectMessage, setCollectMessage] = useState(null);
  const [page, setPage] = useState(0);
  const [indexFilter, setIndexFilter] = useState('');
  const [indexes, setIndexes] = useState([]);

  // CSV import state
  const [importModalOpen, setImportModalOpen] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [importing, setImporting] = useState(false);
  const [importMessage, setImportMessage] = useState(null);
  const [importError, setImportError] = useState(null);
  const fileInputRef = useRef(null);

  // Add / Edit modal state. `null` means closed; otherwise the modal is
  // open and `editingRow` is the source row when editing or `null`
  // when creating a new row. `form` holds the editable fields in a
  // flat object so a single setForm covers every input.
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editingRow, setEditingRow] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [saveMessage, setSaveMessage] = useState(null);
  const [form, setForm] = useState(() => emptyMarketBreadthForm());

  // Power User Mode is re-checked on every render (and after `load()`)
  // so the Edit / Add Row buttons appear or disappear without a full
  // reload when the admin toggles the flag on /profile.
  const [powerUserMode, setPowerUserMode] = useState(() => isPowerUserModeActive());

  // React to localStorage flips from another tab (or another page in
  // this same SPA — though the custom `storage` event only fires for
  // cross-tab changes, we listen for completeness).
  useEffect(() => {
    const onStorage = (evt) => {
      if (evt.key && evt.key !== 'wavewise.powerUserMode') return;
      setPowerUserMode(isPowerUserModeActive());
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  const totalPages = Math.ceil(totalCount / PAGE_SIZE);

  useEffect(() => {
    apiClient.getJson('/api/custom-market-data/daily/indexes')
      .then(res => res.json())
      .then(data => setIndexes(Array.isArray(data) ? data : []))
      .catch(() => setIndexes([]));
  }, []);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const body = {
        Offset: page * PAGE_SIZE,
        Limit: PAGE_SIZE,
        SkipTotalCount: false,
        OrderBy: 'DateDesc',
      };
      if (indexFilter) body.Index = indexFilter;

      const res = await apiClient.postJson('/api/custom-market-data/daily/search', body);
      const data = await res.json();
      setRows(data.Items || []);
      setTotalCount(data.TotalCount || 0);
      // Refresh Power User Mode flag on every successful load so the
      // Edit / Add Row buttons reflect the latest toggle without a
      // full page reload — useful when the admin toggled the flag on
      // /profile and then navigated back here.
      setPowerUserMode(isPowerUserModeActive());
    } catch (err) {
      setError(err.message || 'Failed to load market breadth data');
    } finally {
      setLoading(false);
    }
  }, [page, indexFilter]);

  useEffect(() => {
    load();
  }, [load]);

  const handleIndexFilterChange = e => {
    setIndexFilter(e.target.value);
    setPage(0);
  };

  const handleCollect = async () => {
    try {
      setCollecting(true);
      setCollectMessage(null);
      setError(null);
      await apiClient.postJson('/api/custom-market-data/daily/collect', {});
      setCollectMessage('Collection job queued. Data will appear shortly.');
    } catch (err) {
      setError(err.message || 'Failed to queue collection job');
    } finally {
      setCollecting(false);
    }
  };

  const openImportModal = () => {
    setImportFile(null);
    setImportMessage(null);
    setImportError(null);
    setImportModalOpen(true);
  };

  const closeImportModal = () => {
    if (importing) return;
    setImportModalOpen(false);
  };

  const handleImportFileChange = e => {
    setImportFile(e.target.files[0] || null);
    setImportError(null);
  };

  const handleImport = async () => {
    if (!importFile) {
      setImportError('Please select a file.');
      return;
    }
    try {
      setImporting(true);
      setImportError(null);
      setImportMessage(null);

      const formData = new FormData();
      formData.append('file', importFile);

      const url = '/api/custom-market-data/daily/import-csv';
      const res = await fetch(url, {
        method: 'POST',
        credentials: 'same-origin',
        body: formData,
      });

      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.message || `Server error: ${res.status}`);
      }

      setImportMessage('Import successful.');
      setImportFile(null);
      if (fileInputRef.current) fileInputRef.current.value = '';
      load();
    } catch (err) {
      setImportError(err.message || 'Import failed.');
    } finally {
      setImporting(false);
    }
  };

  // ------------------------------------------------------------------
  // Add / Edit modal
  // ------------------------------------------------------------------

  // Convert a row from the wire format into the form's editable shape.
  // Numbers come back as native JS numbers (or null) from the API; the
  // form expects strings for the numeric <Input>s so a blank value
  // renders as empty. Dates come back as ISO timestamps — we slice off
  // the time portion so the HTML5 date picker shows a clean yyyy-MM-dd.
//
// `Collected` is intentionally absent — the controller forces it to
// FALSE on every admin save, so the form does not carry it either.
const rowToForm = row => ({
    Date: row.Date ? row.Date.slice(0, 10) : '',
    Index: row.Index || '',
    Advances: row.Advances != null ? String(row.Advances) : '',
    Declines: row.Declines != null ? String(row.Declines) : '',
    Unchanged: row.Unchanged != null ? String(row.Unchanged) : '',
    AdvancesVolume: row.AdvancesVolume != null ? String(row.AdvancesVolume) : '',
    DeclinesVolume: row.DeclinesVolume != null ? String(row.DeclinesVolume) : '',
    TotalVolume: row.TotalVolume != null ? String(row.TotalVolume) : '',
    NewHighs: row.NewHighs != null ? String(row.NewHighs) : '',
    NewLows: row.NewLows != null ? String(row.NewLows) : '',
    HiLo: row.HiLo != null ? String(row.HiLo) : '',
  });

  const openAddModal = () => {
    // Defence in depth — the Add Row button is already gated by
    // `powerUserMode` in the JSX, but a stale closure or future code
    // path could still call this. Re-check the persisted flag here so
    // a non-admin or off-mode session cannot accidentally surface the
    // modal.
    if (!isPowerUserModeActive()) return;
    setEditingRow(null);
    setForm(emptyMarketBreadthForm());
    setSaveError(null);
    setSaveMessage(null);
    setEditModalOpen(true);
  };

  const openEditModal = row => {
    // See openAddModal — defence in depth against bypassing the
    // Power User Mode gate.
    if (!isPowerUserModeActive()) return;
    setEditingRow(row);
    setForm(rowToForm(row));
    setSaveError(null);
    setSaveMessage(null);
    setEditModalOpen(true);
  };

  const closeEditModal = () => {
    if (saving) return;
    setEditModalOpen(false);
    setEditingRow(null);
  };

  const handleFormChange = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
  };

  // Convert a numeric form field. Blank input maps to null for nullable
  // columns and 0 for NOT NULL ones. Throws when the user typed junk
  // so the controller can surface a 400 instead of silently writing 0.
  const toInt = (raw, allowNull) => {
    if (raw === '' || raw == null) return allowNull ? null : 0;
    const n = Number(raw);
    if (!Number.isFinite(n) || !Number.isInteger(n))
      throw new Error(`Invalid integer value: '${raw}'`);
    return n;
  };

  const toLong = raw => {
    if (raw === '' || raw == null) return null;
    const n = Number(raw);
    if (!Number.isFinite(n))
      throw new Error(`Invalid integer value: '${raw}'`);
    return n;
  };

  const validateForm = () => {
    if (!form.Date) return 'Date is required.';
    if (!form.Index || !form.Index.trim()) return 'Index is required.';
    const numericFields = ['Advances', 'Declines', 'NewHighs', 'NewLows'];
    for (const f of numericFields) {
      if (form[f] === '') return `${f} is required.`;
      if (Number(form[f]) < 0) return `${f} must be non-negative.`;
    }
    return null;
  };

  const handleSave = async () => {
    const validation = validateForm();
    if (validation) {
      setSaveError(validation);
      return;
    }
    try {
      setSaving(true);
      setSaveError(null);
      setSaveMessage(null);

      const payload = {
        Date: new Date(form.Date + 'T00:00:00Z').toISOString(),
        Index: form.Index.trim(),
        Advances: toInt(form.Advances, false),
        Declines: toInt(form.Declines, false),
        Unchanged: toInt(form.Unchanged, true),
        AdvancesVolume: toLong(form.AdvancesVolume),
        DeclinesVolume: toLong(form.DeclinesVolume),
        TotalVolume: toLong(form.TotalVolume),
        NewHighs: toInt(form.NewHighs, false),
        NewLows: toInt(form.NewLows, false),
        HiLo: toInt(form.HiLo, true),
        // Collected is intentionally omitted — the controller forces
        // it to FALSE on every admin save, so there is no value to
        // send.
      };

      const res = await apiClient.postJson('/api/custom-market-data/daily/save', payload);
      const saved = await res.json();
      setSaveMessage(editingRow ? 'Row updated.' : 'Row added.');
      // Refresh the list so the persisted state (UserData=true,
      // updated values) is reflected immediately. The toast above
      // stays visible until the admin dismisses it.
      load();
      // Update the modal's local state so a second "Save" is a no-op
      // rather than a re-write of the just-persisted values.
      setEditingRow(saved);
      setForm(rowToForm(saved));
    } catch (err) {
      setSaveError(err.message || 'Save failed.');
    } finally {
      setSaving(false);
    }
  };

  const formatNumber = n => (n == null ? '—' : n.toLocaleString());
  const formatDate = d => d ? new Date(d).toLocaleDateString('en-CA') : '—';

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-2">
        <div className="d-flex align-items-center gap-2">
          <span className="text-muted small">
            {loading ? 'Loading…' : `${totalCount.toLocaleString()} records`}
          </span>
          {indexes.length > 0 && (
            <Input
              type="select"
              bsSize="sm"
              style={{ width: 'auto', minWidth: 160 }}
              value={indexFilter}
              onChange={handleIndexFilterChange}
              disabled={loading}
            >
              <option value="">&lt;All&gt;</option>
              {indexes.map(idx => (
                <option key={idx} value={idx}>{idx}</option>
              ))}
            </Input>
          )}
        </div>
        <div className="d-flex gap-2">
          <Button
            size="sm"
            color="primary"
            onClick={handleCollect}
            disabled={collecting || loading}
          >
            {collecting ? <><Spinner size="sm" className="me-1" />Queuing…</> : '↓ Collect'}
          </Button>
          {powerUserMode && (
            <Button
              size="sm"
              color="info"
              onClick={openAddModal}
              disabled={collecting || loading}
              title="Add a new MarketBreadth row (Power User Mode)"
            >
              + Add Row
            </Button>
          )}
          <Button
            size="sm"
            color="success"
            onClick={openImportModal}
            disabled={collecting || loading}
          >
            ↑ Import from CSV
          </Button>
          <Button size="sm" color="secondary" outline onClick={load} disabled={loading}>
            ↻ Refresh
          </Button>
        </div>
      </div>

      <Alert color="success" isOpen={!!collectMessage} toggle={() => setCollectMessage(null)} fade={false} className="mb-2">
        {collectMessage}
      </Alert>
      <Alert color="danger" isOpen={!!error} toggle={() => setError(null)} fade={false}>{error}</Alert>

      <Modal isOpen={importModalOpen} toggle={closeImportModal} size="lg">
        <ModalHeader toggle={closeImportModal}>Import from CSV</ModalHeader>
        <ModalBody>
          <p className="text-muted small mb-2">Select a CSV file to import. Rows are upserted based on the file name.</p>
          <ul className="text-muted small mb-3">
            <li><strong>AMERICAN.csv</strong> — Index = <strong>NYSE American</strong></li>
            <li><strong>NASDAQ.csv</strong> — Index = <strong>NASDAQ</strong></li>
            <li><strong>NEWYORK.csv</strong> — Index = <strong>NYSE</strong></li>
            <li><strong>OTC_DATA.csv</strong> — populates candle Close price for ticker <strong>I:VALUG</strong></li>
            <li>
              <strong>HI-LO.csv</strong> — does multiple things:
              <ul>
                <li>inserts a row into <code>MarketBreadth</code> (Index = <strong>NYSE</strong>) only for dates that don't already exist, populating <strong>NY ADV</strong> → <code>Advances</code>, <strong>NY DEC</strong> → <code>Declines</code>, <strong>NY HIGHS</strong> → <code>NewHighs</code>, <strong>NY LOWS</strong> → <code>NewLows</code> (<code>Unchanged</code> and the volume columns stay <code>NULL</code>);</li>
                <li>also updates New Highs/Lows for existing rows;</li>
                <li>also saves <strong>OTC HI-LO</strong> → <code>HiLo</code> for Index = <strong>NASDAQ</strong>;</li>
                <li>also stores new candles for ticker <strong>I:NYA</strong> for MarketWatch source (Close price)</li>
              </ul>
            </li>
            <li className="text-muted">All CSV imports set <code>Collected = false</code>; only the WSJ collector sets <code>Collected = true</code>.</li>
          </ul>
          <FormGroup>
            <Label for="csvFileInput">CSV File</Label>
            <Input
              type="file"
              id="csvFileInput"
              accept=".csv"
              innerRef={fileInputRef}
              onChange={handleImportFileChange}
              disabled={importing}
            />
          </FormGroup>
          {importError && <Alert color="danger" className="mb-0">{importError}</Alert>}
          {importMessage && <Alert color="success" className="mb-0">{importMessage}</Alert>}
        </ModalBody>
        <ModalFooter>
          <Button color="success" onClick={handleImport} disabled={importing || !importFile}>
            {importing ? <><Spinner size="sm" className="me-1" />Importing…</> : 'Import'}
          </Button>
          <Button color="secondary" outline onClick={closeImportModal} disabled={importing}>
            Close
          </Button>
        </ModalFooter>
      </Modal>

      <Modal isOpen={editModalOpen} toggle={closeEditModal} size="lg">
        <ModalHeader toggle={closeEditModal}>
          {editingRow ? `Edit MarketBreadth row — ${editingRow.Index} / ${editingRow.Date.slice(0, 10)}` : 'Add MarketBreadth row'}
        </ModalHeader>
        <ModalBody>
          <Row>
            <Col md={6}>
              <FormGroup>
                <Label for="mbDate">Date</Label>
                <Input
                  type="date"
                  id="mbDate"
                  value={form.Date}
                  onChange={e => handleFormChange('Date', e.target.value)}
                  disabled={saving}
                />
              </FormGroup>
            </Col>
            <Col md={6}>
              <FormGroup>
                <Label for="mbIndex">Index</Label>
                <Input
                  type="text"
                  id="mbIndex"
                  value={form.Index}
                  onChange={e => handleFormChange('Index', e.target.value)}
                  disabled={saving}
                  placeholder="NYSE / NASDAQ / NYSE American"
                  list="mb-index-options"
                  autoComplete="off"
                />
                <datalist id="mb-index-options">
                  {indexes.map(idx => (
                    <option key={idx} value={idx}>{idx}</option>
                  ))}
                </datalist>
              </FormGroup>
            </Col>
          </Row>
          <Row>
            <Col md={4}>
              <FormGroup>
                <Label for="mbAdvances">Advances</Label>
                <Input
                  type="number"
                  min="0"
                  id="mbAdvances"
                  value={form.Advances}
                  onChange={e => handleFormChange('Advances', e.target.value)}
                  disabled={saving}
                />
              </FormGroup>
            </Col>
            <Col md={4}>
              <FormGroup>
                <Label for="mbDeclines">Declines</Label>
                <Input
                  type="number"
                  min="0"
                  id="mbDeclines"
                  value={form.Declines}
                  onChange={e => handleFormChange('Declines', e.target.value)}
                  disabled={saving}
                />
              </FormGroup>
            </Col>
            <Col md={4}>
              <FormGroup>
                <Label for="mbUnchanged">Unchanged <span className="text-muted">(optional)</span></Label>
                <Input
                  type="number"
                  min="0"
                  id="mbUnchanged"
                  value={form.Unchanged}
                  onChange={e => handleFormChange('Unchanged', e.target.value)}
                  disabled={saving}
                />
              </FormGroup>
            </Col>
          </Row>
          <Row>
            <Col md={4}>
              <FormGroup>
                <Label for="mbNewHighs">New Highs</Label>
                <Input
                  type="number"
                  min="0"
                  id="mbNewHighs"
                  value={form.NewHighs}
                  onChange={e => handleFormChange('NewHighs', e.target.value)}
                  disabled={saving}
                />
              </FormGroup>
            </Col>
            <Col md={4}>
              <FormGroup>
                <Label for="mbNewLows">New Lows</Label>
                <Input
                  type="number"
                  min="0"
                  id="mbNewLows"
                  value={form.NewLows}
                  onChange={e => handleFormChange('NewLows', e.target.value)}
                  disabled={saving}
                />
              </FormGroup>
            </Col>
            <Col md={4}>
              <FormGroup>
                <Label for="mbHiLo">HI-LO <span className="text-muted">(optional)</span></Label>
                <Input
                  type="number"
                  id="mbHiLo"
                  value={form.HiLo}
                  onChange={e => handleFormChange('HiLo', e.target.value)}
                  disabled={saving}
                />
              </FormGroup>
            </Col>
          </Row>
          <Row>
            <Col md={4}>
              <FormGroup>
                <Label for="mbAdvancesVolume">Advances Vol. <span className="text-muted">(optional)</span></Label>
                <Input
                  type="number"
                  min="0"
                  id="mbAdvancesVolume"
                  value={form.AdvancesVolume}
                  onChange={e => handleFormChange('AdvancesVolume', e.target.value)}
                  disabled={saving}
                />
              </FormGroup>
            </Col>
            <Col md={4}>
              <FormGroup>
                <Label for="mbDeclinesVolume">Declines Vol. <span className="text-muted">(optional)</span></Label>
                <Input
                  type="number"
                  min="0"
                  id="mbDeclinesVolume"
                  value={form.DeclinesVolume}
                  onChange={e => handleFormChange('DeclinesVolume', e.target.value)}
                  disabled={saving}
                />
              </FormGroup>
            </Col>
            <Col md={4}>
              <FormGroup>
                <Label for="mbTotalVolume">Total Vol. <span className="text-muted">(optional)</span></Label>
                <Input
                  type="number"
                  min="0"
                  id="mbTotalVolume"
                  value={form.TotalVolume}
                  onChange={e => handleFormChange('TotalVolume', e.target.value)}
                  disabled={saving}
                />
              </FormGroup>
            </Col>
          </Row>
          {saveError && <Alert color="danger" className="mt-3 mb-0">{saveError}</Alert>}
          {saveMessage && <Alert color="success" className="mt-3 mb-0">{saveMessage}</Alert>}
        </ModalBody>
        <ModalFooter>
          <Button color="primary" onClick={handleSave} disabled={saving}>
            {saving ? <><Spinner size="sm" className="me-1" />Saving…</> : (editingRow ? 'Save changes' : 'Add row')}
          </Button>
          <Button color="secondary" outline onClick={closeEditModal} disabled={saving}>
            Close
          </Button>
        </ModalFooter>
      </Modal>

      {loading && rows.length === 0 ? (
        <div className="text-center py-5">
          <Spinner />
        </div>
      ) : (
        <>
          <Table bordered hover responsive size="sm">
            <thead className="table-dark">
              <tr>
                <th>Date</th>
                <th>Index</th>
                <th>Collected</th>
                <th>UserData</th>
                <th className="text-end">Advances</th>
                <th className="text-end">Declines</th>
                <th className="text-end">Unchanged</th>
                <th className="text-end">New Highs</th>
                <th className="text-end">New Lows</th>
                <th className="text-end">HI-LO</th>
                <th className="text-end">Advances Vol.</th>
                <th className="text-end">Declines Vol.</th>
                <th className="text-end">Total Vol.</th>
                <th className="text-end">Actions</th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 ? (
                <tr>
                  <td colSpan={14} className="text-center text-muted py-4">No data</td>
                </tr>
              ) : rows.map(row => (
                <tr key={`${row.Date}-${row.Index}`}>
                  <td>{formatDate(row.Date)}</td>
                  <td>{row.Index}</td>
                  <td>
                    {row.Collected
                      ? <span className="badge bg-success" title="Produced by the live WSJ collector">Yes</span>
                      : <span className="badge bg-secondary" title="Imported from CSV">No</span>}
                  </td>
                  <td>
                    {row.UserData
                      ? <span className="badge bg-info text-dark" title="Added or edited by an admin through the Custom Market Data UI">Yes</span>
                      : <span className="badge bg-light text-muted border" title="Not touched by an admin">No</span>}
                  </td>
                  <td className="text-end text-success">{formatNumber(row.Advances)}</td>
                  <td className="text-end text-danger">{formatNumber(row.Declines)}</td>
                  <td className="text-end text-muted">{formatNumber(row.Unchanged)}</td>
                  <td className="text-end text-success fw-semibold">{formatNumber(row.NewHighs)}</td>
                  <td className="text-end text-danger fw-semibold">{formatNumber(row.NewLows)}</td>
                  <td className="text-end">{row.HiLo == null ? '—' : row.HiLo.toLocaleString()}</td>
                  <td className="text-end">{formatNumber(row.AdvancesVolume)}</td>
                  <td className="text-end">{formatNumber(row.DeclinesVolume)}</td>
                  <td className="text-end">{formatNumber(row.TotalVolume)}</td>
                  <td className="text-end">
                    {powerUserMode && (
                      <Button
                        size="sm"
                        color="primary"
                        outline
                        onClick={() => openEditModal(row)}
                        title="Edit this MarketBreadth row (Power User Mode)"
                      >
                        Edit
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>

          {totalPages > 1 && (
            <Row className="align-items-center mt-2">
              <Col xs="auto">
                <Button
                  size="sm"
                  color="secondary"
                  outline
                  disabled={page === 0 || loading}
                  onClick={() => setPage(0)}
                >
                  « First
                </Button>
              </Col>
              <Col xs="auto">
                <Button
                  size="sm"
                  color="secondary"
                  outline
                  disabled={page === 0 || loading}
                  onClick={() => setPage(p => p - 1)}
                >
                  ‹ Prev
                </Button>
              </Col>
              <Col className="text-center text-muted small">
                Page {page + 1} of {totalPages}
              </Col>
              <Col xs="auto">
                <Button
                  size="sm"
                  color="secondary"
                  outline
                  disabled={page >= totalPages - 1 || loading}
                  onClick={() => setPage(p => p + 1)}
                >
                  Next ›
                </Button>
              </Col>
              <Col xs="auto">
                <Button
                  size="sm"
                  color="secondary"
                  outline
                  disabled={page >= totalPages - 1 || loading}
                  onClick={() => setPage(totalPages - 1)}
                >
                  Last »
                </Button>
              </Col>
            </Row>
          )}
        </>
      )}
    </div>
  );
};

const MarketBreadthWeeklyTable = () => {
  const [rows, setRows] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [collecting, setCollecting] = useState(false);
  const [error, setError] = useState(null);
  const [collectMessage, setCollectMessage] = useState(null);
  const [page, setPage] = useState(0);
  const [indexFilter, setIndexFilter] = useState('');
  const [indexes, setIndexes] = useState([]);

  // CSV import state
  const [importModalOpen, setImportModalOpen] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [importing, setImporting] = useState(false);
  const [importMessage, setImportMessage] = useState(null);
  const [importError, setImportError] = useState(null);
  const fileInputRef = useRef(null);

  const totalPages = Math.ceil(totalCount / PAGE_SIZE);

  useEffect(() => {
    apiClient.getJson('/api/custom-market-data/weekly/indexes')
      .then(res => res.json())
      .then(data => setIndexes(Array.isArray(data) ? data : []))
      .catch(() => setIndexes([]));
  }, []);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const body = {
        Offset: page * PAGE_SIZE,
        Limit: PAGE_SIZE,
        SkipTotalCount: false,
        OrderBy: 'DateDesc',
      };
      if (indexFilter) body.Index = indexFilter;

      const res = await apiClient.postJson('/api/custom-market-data/weekly/search', body);
      const data = await res.json();
      setRows(data.Items || []);
      setTotalCount(data.TotalCount || 0);
    } catch (err) {
      setError(err.message || 'Failed to load weekly market breadth data');
    } finally {
      setLoading(false);
    }
  }, [page, indexFilter]);

  useEffect(() => {
    load();
  }, [load]);

  const handleIndexFilterChange = e => {
    setIndexFilter(e.target.value);
    setPage(0);
  };

  const handleCollect = async () => {
    try {
      setCollecting(true);
      setCollectMessage(null);
      setError(null);
      await apiClient.postJson('/api/custom-market-data/weekly/collect', {});
      setCollectMessage('Collection job queued. Data will appear shortly.');
    } catch (err) {
      setError(err.message || 'Failed to queue collection job');
    } finally {
      setCollecting(false);
    }
  };

  const openImportModal = () => {
    setImportFile(null);
    setImportMessage(null);
    setImportError(null);
    setImportModalOpen(true);
  };

  const closeImportModal = () => {
    if (importing) return;
    setImportModalOpen(false);
  };

  const handleImportFileChange = e => {
    setImportFile(e.target.files[0] || null);
    setImportError(null);
  };

  const handleImport = async () => {
    if (!importFile) {
      setImportError('Please select a file.');
      return;
    }
    try {
      setImporting(true);
      setImportError(null);
      setImportMessage(null);

      const formData = new FormData();
      formData.append('file', importFile);

      const url = '/api/custom-market-data/weekly/import-csv';
      const res = await fetch(url, {
        method: 'POST',
        credentials: 'same-origin',
        body: formData,
      });

      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.message || `Server error: ${res.status}`);
      }

      setImportMessage('Import successful.');
      setImportFile(null);
      if (fileInputRef.current) fileInputRef.current.value = '';
      load();
    } catch (err) {
      setImportError(err.message || 'Import failed.');
    } finally {
      setImporting(false);
    }
  };

  const formatNumber = n => (n == null ? '—' : n.toLocaleString());
  const formatDate = d => d ? new Date(d).toLocaleDateString('en-CA') : '—';

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-2">
        <div className="d-flex align-items-center gap-2">
          <span className="text-muted small">
            {loading ? 'Loading…' : `${totalCount.toLocaleString()} records`}
          </span>
          {indexes.length > 0 && (
            <Input
              type="select"
              bsSize="sm"
              style={{ width: 'auto', minWidth: 160 }}
              value={indexFilter}
              onChange={handleIndexFilterChange}
              disabled={loading}
            >
              <option value="">&lt;All&gt;</option>
              {indexes.map(idx => (
                <option key={idx} value={idx}>{idx}</option>
              ))}
            </Input>
          )}
        </div>
        <div className="d-flex gap-2">
          <Button
            size="sm"
            color="primary"
            onClick={handleCollect}
            disabled={collecting || loading}
          >
            {collecting ? <><Spinner size="sm" className="me-1" />Queuing…</> : '↓ Collect'}
          </Button>
          <Button
            size="sm"
            color="success"
            onClick={openImportModal}
            disabled={collecting || loading}
          >
            ↑ Import from CSV
          </Button>
          <Button size="sm" color="secondary" outline onClick={load} disabled={loading}>
            ↻ Refresh
          </Button>
        </div>
      </div>

      <Alert color="success" isOpen={!!collectMessage} toggle={() => setCollectMessage(null)} fade={false} className="mb-2">
        {collectMessage}
      </Alert>
      <Alert color="danger" isOpen={!!error} toggle={() => setError(null)} fade={false}>{error}</Alert>

      <Modal isOpen={importModalOpen} toggle={closeImportModal} size="lg">
        <ModalHeader toggle={closeImportModal}>Import from CSV</ModalHeader>
        <ModalBody>
          <p className="text-muted small mb-2">Select a CSV file to import. Rows are upserted: inserted when missing, updated when <code>Collected = false</code>, and skipped entirely when <code>Collected = true</code> (WSJ-collected weekly rows are never overwritten by an import).</p>
          <ul className="text-muted small mb-3">
            <li>
              <strong>BREADTH.csv</strong> — comma-separated. Inserted into <code>MarketBreadthWeekly</code> with <code>Index = <strong>NYSE</strong></code>.
              <ul>
                <li><strong>DATE</strong> → <code>Date</code></li>
                <li><strong>ADV</strong> → <code>Advances</code></li>
                <li><strong>DEC</strong> → <code>Declines</code></li>
                <li><strong>HIGHS</strong> → <code>NewHighs</code></li>
                <li><strong>LOWS</strong> → <code>NewLows</code></li>
                <li><strong>AVOL</strong> → <code>AdvancesVolume</code> (×10000 — CSV volume is in ten-thousands)</li>
                <li><strong>DVOL</strong> → <code>DeclinesVolume</code> (×10000)</li>
                <li><strong>ACTIVE UP</strong> / <strong>ACTIVE DN</strong> — skipped (not stored)</li>
              </ul>
            </li>
            <li className="text-muted">Columns not provided by the file (<code>Unchanged</code>, <code>TotalVolume</code>, <code>BlockTrades</code>, <code>Z*</code> volumes) are stored as 0.</li>
          </ul>
          <FormGroup>
            <Label for="weeklyCsvFileInput">CSV File</Label>
            <Input
              type="file"
              id="weeklyCsvFileInput"
              accept=".csv"
              innerRef={fileInputRef}
              onChange={handleImportFileChange}
              disabled={importing}
            />
          </FormGroup>
          {importError && <Alert color="danger" className="mb-0">{importError}</Alert>}
          {importMessage && <Alert color="success" className="mb-0">{importMessage}</Alert>}
        </ModalBody>
        <ModalFooter>
          <Button color="success" onClick={handleImport} disabled={importing || !importFile}>
            {importing ? <><Spinner size="sm" className="me-1" />Importing…</> : 'Import'}
          </Button>
          <Button color="secondary" outline onClick={closeImportModal} disabled={importing}>
            Close
          </Button>
        </ModalFooter>
      </Modal>

      {loading && rows.length === 0 ? (
        <div className="text-center py-5">
          <Spinner />
        </div>
      ) : (
        <>
          <Table bordered hover responsive size="sm">
            <thead className="table-dark">
              <tr>
                <th>Week of</th>
                <th>Index</th>
                <th>Collected</th>
                <th className="text-end">Issues</th>
                <th className="text-end">Advances</th>
                <th className="text-end">Declines</th>
                <th className="text-end">Unchanged</th>
                <th className="text-end">New Highs</th>
                <th className="text-end">New Lows</th>
                <th className="text-end">Advances Vol.</th>
                <th className="text-end">Declines Vol.</th>
                <th className="text-end">Total Vol.</th>
                <th className="text-end">Block Trades</th>
                <th className="text-end">Z Adv. Vol.</th>
                <th className="text-end">Z Decl. Vol.</th>
                <th className="text-end">Z Total Vol.</th>
                <th className="text-end">Z Block Trades</th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 ? (
                <tr>
                  <td colSpan={17} className="text-center text-muted py-4">No data</td>
                </tr>
              ) : rows.map(row => (
                <tr key={`${row.Date}-${row.Index}`}>
                  <td>{formatDate(row.Date)}</td>
                  <td>{row.Index}</td>
                  <td>
                    {row.Collected
                      ? <span className="badge bg-success" title="Produced by the live WSJ collector">Yes</span>
                      : <span className="badge bg-secondary" title="Imported from CSV">No</span>}
                  </td>
                  <td className="text-end text-muted">{formatNumber(row.IssuesTraded)}</td>
                  <td className="text-end text-success">{formatNumber(row.Advances)}</td>
                  <td className="text-end text-danger">{formatNumber(row.Declines)}</td>
                  <td className="text-end text-muted">{formatNumber(row.Unchanged)}</td>
                  <td className="text-end text-success fw-semibold">{formatNumber(row.NewHighs)}</td>
                  <td className="text-end text-danger fw-semibold">{formatNumber(row.NewLows)}</td>
                  <td className="text-end">{formatNumber(row.AdvancesVolume)}</td>
                  <td className="text-end">{formatNumber(row.DeclinesVolume)}</td>
                  <td className="text-end">{formatNumber(row.TotalVolume)}</td>
                  <td className="text-end">{formatNumber(row.BlockTrades)}</td>
                  <td className="text-end text-muted">{formatNumber(row.ZAdvancesVolume)}</td>
                  <td className="text-end text-muted">{formatNumber(row.ZDeclinesVolume)}</td>
                  <td className="text-end text-muted">{formatNumber(row.ZTotalVolume)}</td>
                  <td className="text-end text-muted">{formatNumber(row.ZBlockTrades)}</td>
                </tr>
              ))}
            </tbody>
          </Table>

          {totalPages > 1 && (
            <Row className="align-items-center mt-2">
              <Col xs="auto">
                <Button
                  size="sm"
                  color="secondary"
                  outline
                  disabled={page === 0 || loading}
                  onClick={() => setPage(0)}
                >
                  « First
                </Button>
              </Col>
              <Col xs="auto">
                <Button
                  size="sm"
                  color="secondary"
                  outline
                  disabled={page === 0 || loading}
                  onClick={() => setPage(p => p - 1)}
                >
                  ‹ Prev
                </Button>
              </Col>
              <Col className="text-center text-muted small">
                Page {page + 1} of {totalPages}
              </Col>
              <Col xs="auto">
                <Button
                  size="sm"
                  color="secondary"
                  outline
                  disabled={page >= totalPages - 1 || loading}
                  onClick={() => setPage(p => p + 1)}
                >
                  Next ›
                </Button>
              </Col>
              <Col xs="auto">
                <Button
                  size="sm"
                  color="secondary"
                  outline
                  disabled={page >= totalPages - 1 || loading}
                  onClick={() => setPage(totalPages - 1)}
                >
                  Last »
                </Button>
              </Col>
            </Row>
          )}
        </>
      )}
    </div>
  );
};
